import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  PolicyDateOptions,
  RenovationEntity,
} from '@core/interfaces/interfaces';
import { ProductType } from '@core/models/api/dsm-types';
import { CoverageModel } from '@core/models/views/coverage.model';
import { ProductsService } from '@core/services/products.service';
import { QuoteService } from '@core/services/quote.service';
import { CoverageEntity } from '@core/store/entities/coverage/coverage.entity';
import { PremiumEntity } from '@core/store/entities/premium/premium.entity';
import { ProductModel } from '@core/store/entities/product/product.model';
import { CoveredLocationEntity } from '@entities/covered-location/covered-location.reducer';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateUtils } from '@shared/utils/date.utils';
import { ComponentChanges } from '@shared/utils/general.utils';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DatepickerInputComponent } from '@shared/components/datepicker-input/datepicker-input.component';
import { TaskService } from '@core/services/task.service';
import { DeleteProductService } from '@core/services/delete-product.service';
import { PriceSummaryDisplayProduct } from '../price-summary-display/price-summary-display-content';

interface RentersCoverageDisplayFormModel {
  effectiveDate: Nullable<string>; // "MM/DD/YYYY"
}

@Component({
    selector: 'nwx-renter-coverage-display',
    templateUrl: './renter-coverage-display.component.html',
    styleUrls: ['./renter-coverage-display.component.scss'],
    standalone: false
})
export class RenterCoverageDisplayComponent
  implements OnChanges, OnDestroy, OnInit
{
  @Input() premiums!: PremiumEntity[];
  @Input() coverages!: CoverageEntity[];
  @Input() product!: ProductModel;
  @Input() coveredLocation!: CoveredLocationEntity;
  @Input() renovations!: RenovationEntity[];
  @Input() maxSizeMini!: boolean;
  @Input() priceSummaryDisplayProduct!: PriceSummaryDisplayProduct;

  form = new FormGroup({
    effectiveDate: new FormControl('', [
      (control) =>
        DatepickerInputComponent.validate(
          control,
          this.policyDateOptions?.minDate || '',
          this.policyDateOptions?.maxDate || ''
        ),
    ]),
  });

  policyDateOptions: Nullable<PolicyDateOptions> = null;

  totalPolicyPremium = 0;
  termLengthMonths = 6;
  formattedAddress = '';
  yearRoofReplaced = 0;
  displayCoverages: CoverageModel[] = [];
  displayDeductibles: CoverageModel[] = [];
  showDatePicker: boolean = false;
  formValueDate: any;
  showPremium = false;

  displayCoverageIds: string[] = [
    'PersonalLiability',
    'MedicalPayments',
    'BasicCoverage',
    'IncreasedLossOfUse',
  ];

  deductibleCodes: string[] = ['AllPerilDeductible'];

  updateInProgress$: Observable<boolean>;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private quoteService: QuoteService,
    private modalService: NgbModal,
    private productsService: ProductsService,
    private ref: ChangeDetectorRef,
    private taskService: TaskService,
    private deleteProductService: DeleteProductService
  ) {
    this.updateInProgress$ = this.quoteService
      .getQuoteUpdateInProgress()
      .pipe(map((v) => !!v));
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.updateInProgress$)
      )
      .subscribe(this.onFormChanged.bind(this));
  }

  ngOnDestroy(): void {
    this.form.disable();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.showDatePicker = false;
  }

  ngOnInit(): void {
    this.productsService
      .getPolicyDateOptions(this.product.type)
      .pipe(debounceTime(100), takeUntil(this.unsubscribe$))
      .subscribe((options) => {
        this.policyDateOptions = options;
        this.ref.detectChanges();
      });
  }

  ngOnChanges(changes: ComponentChanges<RenterCoverageDisplayComponent>): void {
    if (changes.priceSummaryDisplayProduct) {
      if (!this.priceSummaryDisplayProduct.installmentPrice) {
        this.showPremium = false;
      } else {
        this.showPremium = true;
      }
    }
    if (changes.coveredLocation) {
      this.formatAddress();
    }
    if (changes.coverages) {
      this.buildDisplayCoverages();
    }
    if (changes.product) {
      this.form.patchValue({
        effectiveDate: DateUtils.formatDsmDateToOld(
          this.product.effectiveDate || ''
        ),
      });
      this.showDatePicker = false;
    }
  }

  ngDoCheck(): void {
    if (this.formValueDate !== this.form.value.effectiveDate) {
      this.formValueDate = this.form.value.effectiveDate;
    }
  }

  private formatAddress(): void {
    const location = this.coveredLocation.location;
    if (location) {
      this.formattedAddress = `${location.addressLine1}, ${location.city}, ${
        location.state
      } ${location.postalCode?.substring(0, 5)}`;
      const state = location.state?.toUpperCase();
      const addressLine = this.toTitleCase(location.addressLine1 || '');
      const city = this.toTitleCase(location.city || '');
      const postal = this.toTitleCase(location.postalCode || '');
      this.formattedAddress = `${addressLine}, ${city}, ${state} ${postal?.substring(
        0,
        5
      )}`;
    }
  }

  private toTitleCase(text: string): string {
    return text
      .toLocaleLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private buildDisplayCoverages(): void {
    this.displayCoverages = [];
    const displayEntities = this.coverages.filter(
      (c) =>
        c.productId === 'Tenant' &&
        this.displayCoverageIds.includes(c.coverageId)
    );
    displayEntities.forEach((cov) => {
      const val = cov.selectedValue[0]?.value || '';
      if (cov.coverageId === 'IncreasedLossOfUse') {
        this.displayCoverages.push({
          name: cov.name,
          value: cov.selectedValue[0]?.description || '',
        });
      } else if (this.isNumber(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: this.currencyPipe.transform(val) || '',
        });
      } else if (this.isAcceptDecline(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: val === 'true' ? 'Accept' : 'Decline',
        });
      }
    });

    this.displayDeductibles = [];
    const deductibleCoverages = this.coverages.filter(
      (c) =>
        c.productId === 'Tenant' &&
        c.available &&
        !c.selectedValue?.find(
          (v) => v.code === 'selected' && v.value === 'false'
        ) &&
        !this.displayCoverageIds.includes(c.coverageId)
    );
    deductibleCoverages.forEach((cov) => {
      const value =
        cov.selectedValue?.find(
          (v) =>
            v.code === 'AllPerilDeductible' ||
            v.code === 'UnscheduledPersonalProperty'
        )?.value ||
        cov.selectedValue?.[0]?.description ||
        'Selected';
      this.displayDeductibles.push({
        name: cov.name,
        value,
      });
    });
  }

  isNumber(val: string | number): boolean {
    return parseInt(`${val}`, 10) ? true : false;
  }

  isAcceptDecline(val: string): boolean {
    return val === 'false' || val === 'true';
  }

  editCoverages(): void {
    this.router.navigate(['/quote/coverages'], {
      queryParams: { product: 'Tenant' } as { product: ProductType },
    });
  }

  promptToRemovePolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndRemoveProduct(this.product);
  }

  promptToWithdrawPolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndWithdrawProduct(this.product);
  }

  private onFormChanged(
    value: [Partial<RentersCoverageDisplayFormModel>, Nullable<boolean>]
  ): void {
    const updateInprogress = value[1] === true;
    const newDate = DateUtils.formatDateToDSM(value[0]?.effectiveDate);
    if (
      updateInprogress ||
      !this.product ||
      this.product.effectiveDate === newDate ||
      !this.form.valid
    ) {
      return;
    }
    this.productsService.updateQuote({
      productType: 'Tenant',
      effectiveDate: newDate,
    });
  }

  scrollToTasks(): void {
    this.taskService.setShowTasks(true);
    const div = document.querySelector('.tasks-container');
    if (div) div.scrollIntoView({ behavior: 'smooth' });
  }
}
